import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import { getWhatsAppFlows, updatePreLeadList } from '../../services/api/implementation/impl';
import {
  buildScheduleFormState,
  DEFAULT_TIMEZONE,
  TIMEZONE_OPTIONS,
  validateSendDelayMinutes,
  validateWorkingHours,
  WORKING_DAY_OPTIONS,
  workingHoursEqual
} from '../../utils/preleadWhatsAppFlowScheduleUtils';
import { isFlowSelectableForPreleadAutoStart } from '../../utils/whatsappFlowUtils';

function formatFlowOptionLabel(flow) {
  const name = flow?.name || 'Untitled flow';
  const slug = flow?.slug ? ` (${flow.slug})` : '';
  return `${name}${slug}`;
}

function schedulesEqual(saved, current) {
  const savedFlowId = saved.selectedFlowId ?? null;
  const currentFlowId = current.selectedFlowId ?? null;
  if (savedFlowId == null && currentFlowId == null) {
    // both unlinked — still equal
  } else if (savedFlowId == null || currentFlowId == null) {
    return false;
  } else if (Number(savedFlowId) !== Number(currentFlowId)) {
    return false;
  }
  if (Number(saved.delayMinutes) !== Number(current.delayMinutes)) return false;
  const savedUseHours = Boolean(saved.useWorkingHours);
  const currentUseHours = Boolean(current.useWorkingHours);
  if (savedUseHours !== currentUseHours) return false;
  if (!savedUseHours && !currentUseHours) return true;
  return workingHoursEqual(saved.workingHours, current.workingHours);
}

const PreleadListAutoWhatsAppFlow = ({ isOpen, onClose, listId, list, projectId, onLinked }) => {
  const savedState = useMemo(() => buildScheduleFormState(list), [list]);

  const [selectedFlowId, setSelectedFlowId] = useState(savedState.selectedFlowId);
  const [delayMinutes, setDelayMinutes] = useState(savedState.delayMinutes);
  const [useWorkingHours, setUseWorkingHours] = useState(savedState.useWorkingHours);
  const [workingHours, setWorkingHours] = useState(savedState.workingHours);

  const listConfigKey = [
    list?.whatsapp_flow_id,
    list?.whatsapp_flow_send_delay_minutes,
    list?.whatsapp_flow_working_hours?.timezone,
    list?.whatsapp_flow_working_hours?.start,
    list?.whatsapp_flow_working_hours?.end,
    Array.isArray(list?.whatsapp_flow_working_hours?.days)
      ? list.whatsapp_flow_working_hours.days.join(',')
      : ''
  ].join('|');

  useEffect(() => {
    if (!isOpen) return;
    const next = buildScheduleFormState(list);
    setSelectedFlowId(next.selectedFlowId);
    setDelayMinutes(next.delayMinutes);
    setUseWorkingHours(next.useWorkingHours);
    setWorkingHours(next.workingHours);
  }, [isOpen, listConfigKey, list]);

  const currentState = {
    selectedFlowId,
    delayMinutes: Number(delayMinutes) || 0,
    useWorkingHours,
    workingHours
  };
  const isDirty = !schedulesEqual(savedState, currentState);

  const linkedFlow = list?.whatsapp_flow ?? null;
  const hasFlowSelected = selectedFlowId != null;

  const {
    data: flows = [],
    isLoading: isFlowsLoading,
    isError: isFlowsError,
    refetch: refetchFlows
  } = useQuery({
    queryKey: ['whatsappFlows', projectId],
    queryFn: () => getWhatsAppFlows(projectId),
    enabled: Boolean(isOpen && projectId)
  });

  const selectableFlows = useMemo(() => flows.filter(isFlowSelectableForPreleadAutoStart), [flows]);

  const flowOptionsForSelect = useMemo(() => {
    const byId = new Map();
    selectableFlows.forEach((flow) => byId.set(Number(flow.id), flow));
    if (linkedFlow?.id != null && !byId.has(Number(linkedFlow.id))) {
      byId.set(Number(linkedFlow.id), linkedFlow);
    }
    if (selectedFlowId != null && !byId.has(Number(selectedFlowId))) {
      byId.set(Number(selectedFlowId), {
        id: selectedFlowId,
        name: linkedFlow?.name || `Flow #${selectedFlowId}`,
        slug: linkedFlow?.slug
      });
    }
    return Array.from(byId.values());
  }, [selectableFlows, linkedFlow, selectedFlowId]);

  const selectedFlowIsInSelectableList = selectableFlows.some(
    (flow) => Number(flow.id) === Number(selectedFlowId)
  );

  const canPickFlows = Boolean(projectId);
  const canUnlinkOnly = !canPickFlows && savedState.selectedFlowId != null;

  const toggleDay = (day) => {
    setWorkingHours((prev) => {
      const days = prev.days.includes(day)
        ? prev.days.filter((d) => d !== day)
        : [...prev.days, day].sort((a, b) => a - b);
      return { ...prev, days };
    });
  };

  const { mutate: saveConfig, isPending: isSaving } = useMutation({
    mutationFn: (payload) => updatePreLeadList(listId, payload),
    onSuccess: () => {
      const wasUnlink = selectedFlowId == null;
      toast.success(wasUnlink ? 'Flow removed' : 'Settings saved');
      onLinked?.();
      onClose?.();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Could not save');
    }
  });

  const saveDisabled = !isDirty || isSaving || (selectedFlowId != null && !canPickFlows);

  const handleSave = () => {
    if (saveDisabled) return;

    if (selectedFlowId == null) {
      saveConfig({ whatsapp_flow_id: null });
      return;
    }

    const delayError = validateSendDelayMinutes(delayMinutes);
    if (delayError) {
      toast.error(delayError);
      return;
    }

    let payload = {
      whatsapp_flow_id: Number(selectedFlowId),
      whatsapp_flow_send_delay_minutes: Number(delayMinutes) || 0,
      whatsapp_flow_working_hours: null
    };

    if (useWorkingHours) {
      const hoursError = validateWorkingHours(workingHours);
      if (hoursError) {
        toast.error(hoursError);
        return;
      }
      payload.whatsapp_flow_working_hours = {
        timezone: workingHours.timezone || DEFAULT_TIMEZONE,
        days: workingHours.days,
        start: workingHours.start,
        end: workingHours.end
      };
    }

    saveConfig(payload);
  };

  const renderFlowSelect = () => {
    if (!canPickFlows && !canUnlinkOnly) {
      return (
        <select
          id="prelead-list-whatsapp-flow"
          disabled
          className="w-full cursor-not-allowed rounded-lg border border-gray-200 bg-gray-100 px-3 py-2.5 text-sm text-gray-500">
          <option value="">None</option>
        </select>
      );
    }

    if (!canPickFlows && canUnlinkOnly) {
      return (
        <select
          id="prelead-list-whatsapp-flow"
          value={selectedFlowId ?? ''}
          onChange={(e) => {
            const value = e.target.value;
            setSelectedFlowId(value === '' ? null : Number(value));
          }}
          disabled={isSaving}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition-colors focus:border-customBlue focus:ring-1 focus:ring-customBlue disabled:opacity-50">
          <option value="">None</option>
          <option value={savedState.selectedFlowId}>
            {linkedFlow?.name || `Flow #${savedState.selectedFlowId}`}
          </option>
        </select>
      );
    }

    if (isFlowsLoading) {
      return (
        <>
          <select
            id="prelead-list-whatsapp-flow"
            value={selectedFlowId ?? ''}
            disabled
            className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2.5 text-sm text-gray-800 opacity-80">
            <option value="">None</option>
            {flowOptionsForSelect.map((flow) => (
              <option key={flow.id} value={flow.id}>
                {formatFlowOptionLabel(flow)}
              </option>
            ))}
          </select>
          <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading other flows…
          </p>
        </>
      );
    }

    if (isFlowsError) {
      return (
        <div className="flex flex-col gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
          <p>Could not load WhatsApp flows.</p>
          <button
            type="button"
            onClick={() => refetchFlows()}
            className="w-fit rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50">
            Try again
          </button>
        </div>
      );
    }

    return (
      <>
        <select
          id="prelead-list-whatsapp-flow"
          value={selectedFlowId ?? ''}
          onChange={(e) => {
            const value = e.target.value;
            setSelectedFlowId(value === '' ? null : Number(value));
          }}
          disabled={isSaving}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition-colors focus:border-customBlue focus:ring-1 focus:ring-customBlue disabled:opacity-50">
          <option value="">None</option>
          {flowOptionsForSelect.map((flow) => (
            <option key={flow.id} value={flow.id}>
              {formatFlowOptionLabel(flow)}
              {!selectedFlowIsInSelectableList && Number(flow.id) === Number(selectedFlowId)
                ? ' (current)'
                : ''}
            </option>
          ))}
        </select>
        {selectableFlows.length === 0 && !selectedFlowId ? (
          <p className="mt-2 text-xs text-gray-500">No approved Sales flows available for this project.</p>
        ) : null}
      </>
    );
  };

  if (!isOpen) return null;

  return (
    <Modal
      width={55}
      name="Auto WhatsApp flow"
      confirmOnClose
      isDirty={isDirty}
      onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-gray-500">
          When a Facebook lead arrives, WhatsApp is sent after the configured delay, only during
          working hours. Outside hours → next working day at start time. Only approved Sales flows
          are available.
        </p>

        {!canPickFlows ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-600">
            Map this list to a project (via Facebook form) before linking a WhatsApp flow.
            {canUnlinkOnly ? ' You can still remove the current link below.' : ''}
          </div>
        ) : null}

        <div>
          <label
            htmlFor="prelead-list-whatsapp-flow"
            className="mb-1.5 block text-xs font-semibold text-gray-600">
            Flow
          </label>
          {renderFlowSelect()}
        </div>

        {hasFlowSelected ? (
          <>
            <div>
              <label
                htmlFor="prelead-list-send-delay"
                className="mb-1.5 block text-xs font-semibold text-gray-600">
                Delay
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="prelead-list-send-delay"
                  type="number"
                  min={0}
                  max={1440}
                  value={delayMinutes}
                  onChange={(e) => setDelayMinutes(e.target.value)}
                  disabled={isSaving}
                  className="w-24 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition-colors focus:border-customBlue focus:ring-1 focus:ring-customBlue disabled:opacity-50"
                />
                <span className="text-sm text-gray-600">minutes after lead</span>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50/40 px-4 py-3 space-y-3">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <input
                  type="checkbox"
                  checked={useWorkingHours}
                  onChange={(e) => setUseWorkingHours(e.target.checked)}
                  disabled={isSaving}
                  className="h-4 w-4 rounded border-gray-300 text-customBlue focus:ring-customBlue"
                />
                Use working hours
              </label>

              {useWorkingHours ? (
                <div className="space-y-3 pl-0.5">
                  <div>
                    <label
                      htmlFor="prelead-list-timezone"
                      className="mb-1.5 block text-xs font-semibold text-gray-600">
                      Timezone
                    </label>
                    <select
                      id="prelead-list-timezone"
                      value={workingHours.timezone || DEFAULT_TIMEZONE}
                      onChange={(e) =>
                        setWorkingHours((prev) => ({ ...prev, timezone: e.target.value }))
                      }
                      disabled={isSaving}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition-colors focus:border-customBlue focus:ring-1 focus:ring-customBlue disabled:opacity-50">
                      {TIMEZONE_OPTIONS.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <p className="mb-1.5 text-xs font-semibold text-gray-600">Days</p>
                    <div className="flex flex-wrap gap-2">
                      {WORKING_DAY_OPTIONS.map(({ value, label }) => {
                        const checked = workingHours.days.includes(value);
                        return (
                          <label
                            key={value}
                            className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                              checked
                                ? 'border-customBlue bg-customBlue/10 text-customBlue'
                                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                            }`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleDay(value)}
                              disabled={isSaving}
                              className="sr-only"
                            />
                            {label}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="prelead-list-hours-start"
                        className="mb-1.5 block text-xs font-semibold text-gray-600">
                        Start
                      </label>
                      <input
                        id="prelead-list-hours-start"
                        type="time"
                        value={workingHours.start}
                        onChange={(e) =>
                          setWorkingHours((prev) => ({ ...prev, start: e.target.value }))
                        }
                        disabled={isSaving}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition-colors focus:border-customBlue focus:ring-1 focus:ring-customBlue disabled:opacity-50"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="prelead-list-hours-end"
                        className="mb-1.5 block text-xs font-semibold text-gray-600">
                        End
                      </label>
                      <input
                        id="prelead-list-hours-end"
                        type="time"
                        value={workingHours.end}
                        onChange={(e) =>
                          setWorkingHours((prev) => ({ ...prev, end: e.target.value }))
                        }
                        disabled={isSaving}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition-colors focus:border-customBlue focus:ring-1 focus:ring-customBlue disabled:opacity-50"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-500">
                  Unchecked — messages send anytime (only the delay applies).
                </p>
              )}
            </div>
          </>
        ) : null}

        <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
          <Button type="button" displayType="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={saveDisabled} isLoading={isSaving}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PreleadListAutoWhatsAppFlow;
